import chroma from 'chroma-js';

/**
 * CANONICAL WCAG-AA contrast utilities — single source of truth.
 *
 * Every study/instance color adjustment in the app must flow through this
 * module. The previously divergent implementations all delegate here:
 *   - SharedUtilsService.contrastColor      → getAccessibleTextColor
 *   - CommonUtilsService.textContrastColor  → getAccessibleTextColor
 *   - CoreUtilsService.getThemeAwareColor       (delegates)
 *   - CoreUtilsService.getAccessibleTextColor   (delegates)
 *   - CoreUtilsService.getAccessibleButtonColor (delegates)
 *
 * These are intentionally framework-free pure functions so they can live in
 * the lowest-level library (`shared`) and be consumed by `core` and `common`
 * without an import cycle. They read the active theme directly off
 * `document.documentElement`, matching the CSS variables in global.scss.
 *
 * Do NOT fork these into a new 4th implementation. See
 * docs/adr/0001-contrast-utility-consolidation.md.
 */

/** WCAG 2.1 AA contrast ratio for normal body text. */
export const WCAG_AA_NORMAL = 4.5;

/** WCAG 2.1 AA contrast ratio for large text / non-text UI components. */
export const WCAG_AA_LARGE = 3;

function isHighContrast(): boolean {
  return document.documentElement.classList.contains(
    'ion-palette-high-contrast'
  );
}

function isDarkTheme(): boolean {
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

/**
 * Returns the theme background hex for the current DOM state.
 * Matches the CSS variables defined in global.scss.
 */
export function getThemeBackground(): string {
  const isDark = isDarkTheme();
  const isHC = isHighContrast();
  if (isDark) return isHC ? '#000000' : '#1a1a1a';
  return isHC ? '#ffffff' : '#fafafa';
}

/**
 * Returns a theme-aware version of `hex` that is guaranteed to meet the WCAG AA
 * contrast threshold against the active theme background.
 *
 * Behaviour (matches the acceptance criteria):
 *   - The original color is the base reference. If it already meets the
 *     threshold for the active theme, it is returned UNCHANGED (no adjustment,
 *     no brand drift).
 *   - Otherwise the adjustment is purely contrast-driven and applies the
 *     minimum lightness delta needed to cross the threshold, in the
 *     theme-appropriate direction: darker on a light background, lighter on a
 *     dark background. Hue and saturation are preserved.
 *   - Only when lightness alone cannot reach the threshold (high-luminance hues
 *     such as yellow on white) is saturation reduced, with opaque black/white
 *     as the final guaranteed-readable fallback.
 *
 * Threshold: study color is used predominantly as a large UI accent/fill, so
 * the WCAG AA large/non-text ratio (3:1) is used by default; factsheet bloc
 * text is body copy and uses the normal-text ratio (4.5:1). Using the large
 * ratio for the accent keeps typical brand colors untouched and the adjustment
 * subtle, while still guaranteeing a real contrast floor.
 */
export function getThemeAwareColor(hex: string, isFactsheet = false): string {
  const bgHex = getThemeBackground();
  const color = chroma(hex);
  const minContrast = isFactsheet ? WCAG_AA_NORMAL : WCAG_AA_LARGE;

  // AC: no adjustment when the original color already meets the threshold.
  if (chroma.contrast(color, bgHex) >= minContrast) {
    return hex;
  }

  const isDark = chroma(bgHex).luminance() < 0.5;
  const [h, s, l] = color.hsl();

  // Step 1: minimum lightness delta in the theme-appropriate direction
  // (lighter on dark, darker on light) until the threshold is met.
  const step = 0.05;
  const lEdge = isDark ? 0.95 : 0.05;
  let curL = l;
  let candidate = color;
  for (let i = 0; i < 19; i++) {
    curL = isDark ? Math.min(curL + step, lEdge) : Math.max(curL - step, lEdge);
    candidate = isNaN(h) ? color.set('hsl.l', curL) : chroma.hsl(h, s, curL);
    if (chroma.contrast(candidate, bgHex) >= minContrast) {
      return candidate.hex();
    }
    if (curL === lEdge) break;
  }

  // Step 2: lightness alone failed (high-luminance hues like yellow / lime /
  // cyan against white, or saturated darks against black). Hold the extreme
  // lightness and desaturate toward gray until the threshold is crossed.
  if (!isNaN(h)) {
    let curS = s;
    for (let i = 0; i < 12; i++) {
      curS = Math.max(curS - 0.1, 0);
      candidate = chroma.hsl(h, curS, curL);
      if (chroma.contrast(candidate, bgHex) >= minContrast) {
        return candidate.hex();
      }
      if (curS === 0) break;
    }
  }

  // Final fallback: lightness and saturation budgets exhausted — opaque
  // white/black is the only guaranteed-readable result.
  return isDark ? '#ffffff' : '#000000';
}

/**
 * Given a background color, return a foreground text color that meets WCAG AA
 * contrast against it. Prefers the theme's near-black / near-white text tokens
 * (or pure black/white in high-contrast mode); falls back to luminance-stepping
 * a candidate until the threshold is met.
 */
export function getAccessibleTextColor(
  backgroundHex: string,
  minContrast = WCAG_AA_NORMAL
): string {
  const bg = chroma(backgroundHex);

  const isHC = isHighContrast();
  // Preferred candidates
  const darkText = chroma(isHC ? '#000000' : '#383838');
  const lightText = chroma(isHC ? '#ffffff' : '#e8e8e8');

  const lightContrast = chroma.contrast(lightText, bg);
  const darkContrast = chroma.contrast(darkText, bg);

  // Use the best valid contrast
  if (lightContrast >= minContrast || darkContrast >= minContrast) {
    return lightContrast > darkContrast ? lightText.hex() : darkText.hex();
  }

  // If neither passes, dynamically generate one
  const isDarkBg = bg.luminance() < 0.5;
  let text = isDarkBg ? lightText : darkText;

  // Gradually move luminance until contrast is met
  for (let i = 0; i < 10; i++) {
    text = isDarkBg
      ? text.luminance(text.luminance() + 0.05)
      : text.luminance(text.luminance() - 0.05);

    if (chroma.contrast(text, bg) >= minContrast) {
      return text.hex();
    }
  }

  // Hard fallback (guaranteed readable). Return a concrete hex rather than a
  // CSS custom property: this value is assigned directly to element.style.color
  // (via [style.color] / bypassSecurityTrustStyle), where browsers do not
  // resolve var(--…) from the stylesheet cascade — so a var() string would
  // leave text with no effective color in Capacitor webview/modal contexts.
  return isDarkBg ? '#ffffff' : '#000000';
}

/**
 * Get an accessible button color based on background and minimum contrast.
 * Locks hue & saturation and adjusts only lightness until the threshold is met.
 */
export function getAccessibleButtonColor(
  buttonHex: string,
  backgroundHex = '#000000', // Dark Mode + High Contrast: background is always black when used from applyIonicColorToRoot.
  minContrast = WCAG_AA_NORMAL
): string {
  const bg = chroma(backgroundHex);

  // Start in HSL to lock hue & saturation (only lightness `l` is adjusted below)
  const [h, s, initialLightness] = chroma(buttonHex).hsl();
  let l = initialLightness;

  // Edge case: achromatic colors
  if (isNaN(h)) {
    return chroma.contrast(buttonHex, bg) >= minContrast
      ? buttonHex
      : bg.luminance() < 0.5
      ? '#ffffff'
      : '#000000';
  }

  let color = chroma.hsl(h, s, l);

  if (chroma.contrast(color, bg) >= minContrast) {
    return color.hex();
  }

  const brighten = bg.luminance() < 0.5;

  // Adjust ONLY lightness
  for (let i = 0; i < 12; i++) {
    l = brighten ? Math.min(l + 0.05, 0.95) : Math.max(l - 0.05, 0.05);

    color = chroma.hsl(h, s, l);

    if (chroma.contrast(color, bg) >= minContrast) {
      return color.hex();
    }
  }

  // Final fallback – same hue, max safe contrast
  return brighten ? chroma.hsl(h, s, 0.9).hex() : chroma.hsl(h, s, 0.1).hex();
}
